import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter, ReferenceDot,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine, Cell
} from 'recharts';
import { fmt, COLORS } from '../utils';
import ChartCard from './ChartCard';

// Clinical quality measures by field name — with display label, direction, and note
const CLINICAL_MEASURES = {
  qualityid_318:   { label: 'Fall Risk Screening',             short: 'Q318', higher: true,  note: '% patients 65+ screened for fall risk' },
  qualityid_110:   { label: 'Influenza Immunization',          short: 'Q110', higher: true,  note: '% patients receiving influenza immunization' },
  qualityid_226:   { label: 'Tobacco Screening & Cessation',   short: 'Q226', higher: true,  note: '% patients screened for tobacco use' },
  qualityid_134_wi:{ label: 'Depression Screening (WI)',       short: 'Q134', higher: true,  note: '% patients screened for depression with follow-up plan' },
  qualityid_113:   { label: 'Colorectal Cancer Screening',     short: 'Q113', higher: true,  note: '% adults 50-75 with appropriate colorectal cancer screening' },
  qualityid_112:   { label: 'Breast Cancer Screening',         short: 'Q112', higher: true,  note: '% women 50-74 who had a mammogram' },
  qualityid_438:   { label: 'Statin Therapy (Cardiovascular)', short: 'Q438', higher: true,  note: '% high-risk patients prescribed statin therapy' },
  qualityid_370:   { label: 'Depression Remission (12mo)',     short: 'Q370', higher: true,  note: '% patients with depression reaching remission at 12 months' },
  qualityid_001_wi:{ label: 'Diabetes: HbA1c Poor Control',   short: 'Q001', higher: false, note: '% diabetic patients with HbA1c >9% — lower is better' },
  qualityid_236_wi:{ label: 'Controlling High Blood Pressure', short: 'Q236', higher: true,  note: '% hypertensive patients with BP <140/90 mmHg' },
  measure_479:     { label: '30-Day Hospital Readmission Rate',short: 'M479', higher: false, note: 'Risk-adjusted % readmitted within 30 days — lower is better' },
  measure_484:     { label: 'Admissions: Multiple Chronic Conditions', short: 'M484', higher: false, note: 'Risk-standardized admission rate for patients with MCCs — lower is better' },
};

// Which clinical measures are available by year
const CLINICAL_MEASURE_YEARS = {
  2021: ['qualityid_318','qualityid_110','qualityid_226','qualityid_134_wi','qualityid_113','qualityid_112','qualityid_438','qualityid_370','qualityid_001_wi','qualityid_236_wi','measure_479'],
  2022: ['qualityid_318','qualityid_110','qualityid_226','qualityid_134_wi','qualityid_113','qualityid_112','qualityid_438','qualityid_370','qualityid_001_wi','qualityid_236_wi','measure_479','measure_484'],
  2023: ['qualityid_318','qualityid_110','qualityid_226','qualityid_134_wi','qualityid_113','qualityid_112','qualityid_438','qualityid_370','qualityid_001_wi','qualityid_236_wi','measure_479','measure_484'],
};

const CAHPS_LABELS = {
  cahps_1: 'Getting Needed Care',         cahps_2: 'Getting Care Quickly',
  cahps_3: 'How Well Doctors Communicate', cahps_4: "Patients' Rating of Doctor",
  cahps_5: 'Access to Specialists',        cahps_6: 'Health Promotion & Education',
  cahps_7: 'Shared Decision Making',       cahps_8: 'Health Status/Functional Status',
  cahps_9: 'Care Coordination',            cahps_11: 'Courteous & Helpful Staff',
};

const ScatterTip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="custom-tooltip">
      <div className="custom-tooltip-title">{d.aco_name || d.aco_id}</div>
      <div className="custom-tooltip-row"><span>Quality Score</span><span className="custom-tooltip-val">{d.qual_score?.toFixed(1) ?? '—'}</span></div>
      <div className="custom-tooltip-row"><span>Savings Rate</span><span className="custom-tooltip-val">{fmt.pct(d.sav_rate)}</span></div>
      <div className="custom-tooltip-row"><span>Earned Savings</span><span className="custom-tooltip-val">{fmt.dollars(d.earn_save_loss, 2)}</span></div>
    </div>
  );
};

export default function QualityDashboard({ perfData, navigateToACO, pinnedACO }) {
  const [year, setYear]           = useState(2024);
  const [showAllPerf, setShowAll] = useState(false);
  const YEARS = [2014,2015,2016,2017,2018,2019,2020,2021,2022,2023,2024];

  // Records for selected year
  const yearRecs = useMemo(() => {
    if (!perfData) return [];
    return perfData.filter(r => r.performance_year === year && r.is_midyear === 0);
  }, [perfData, year]);

  // Quality score distribution
  const qualDist = useMemo(() => {
    const buckets = [
      { label: '<60',    min: 0,   max: 60  },
      { label: '60–70',  min: 60,  max: 70  },
      { label: '70–80',  min: 70,  max: 80  },
      { label: '80–90',  min: 80,  max: 90  },
      { label: '90–95',  min: 90,  max: 95  },
      { label: '95–100', min: 95,  max: 101 },
    ];
    return buckets.map(b => ({
      label: b.label,
      count: yearRecs.filter(r => r.qual_score != null && r.qual_score >= b.min && r.qual_score < b.max).length,
    }));
  }, [yearRecs]);

  // Quality vs savings scatter
  const qvsSavings = useMemo(() =>
    yearRecs.filter(r => r.qual_score != null && r.sav_rate != null)
      .map(r => ({ ...r, sav_rate_pct: r.sav_rate * 100 })),
  [yearRecs]);

  // CAHPS program-level trend (2021–2024)
  const cahpsTrend = useMemo(() => {
    if (!perfData) return [];
    return [2021, 2022, 2023, 2024].map(yr => {
      const recs = perfData.filter(r => r.performance_year === yr && r.is_midyear === 0);
      const row  = { year: yr };
      Object.keys(CAHPS_LABELS).forEach(field => {
        const vals = recs.map(r => r[field]).filter(v => v != null);
        row[field] = vals.length ? vals.reduce((s,v) => s+v, 0) / vals.length : null;
      });
      return row;
    });
  }, [perfData]);

  // CAHPS snapshot for selected year
  const cahpsSnapshot = useMemo(() => {
    if (!yearRecs.length) return [];
    return Object.entries(CAHPS_LABELS).map(([field, label]) => {
      const vals = yearRecs.map(r => r[field]).filter(v => v != null);
      return { field, label, avg: vals.length ? vals.reduce((s,v) => s+v, 0)/vals.length : null, n: vals.length };
    }).filter(d => d.avg != null);
  }, [yearRecs]);

  // Pinned ACO record (must be declared before clinicalSnapshot)
  const pinnedRec = useMemo(() => {
    if (!pinnedACO) return null;
    return yearRecs.find(r => r.aco_id === pinnedACO.aco_id) || null;
  }, [pinnedACO, yearRecs]);

  // Clinical quality measures snapshot for selected year
  const clinicalSnapshot = useMemo(() => {
    const availableFields = CLINICAL_MEASURE_YEARS[year] || [];
    if (!availableFields.length || !yearRecs.length) return [];
    return availableFields.map(field => {
      const meta = CLINICAL_MEASURES[field];
      if (!meta) return null;
      const vals = yearRecs.map(r => r[field]).filter(v => v != null && !isNaN(Number(v)));
      if (!vals.length) return null;
      const avg = vals.reduce((s, v) => s + Number(v), 0) / vals.length;
      return { field, label: meta.label, avg, n: vals.length, higher: meta.higher, note: meta.note };
    }).filter(Boolean);
  }, [yearRecs, year]);

  // Pinned ACO clinical measures
  const pinnedClinical = useMemo(() => {
    if (!pinnedRec || !clinicalSnapshot.length) return {};
    return Object.fromEntries(
      clinicalSnapshot.map(m => [m.field, pinnedRec[m.field] != null ? Number(pinnedRec[m.field]) : null])
    );
  }, [pinnedRec, clinicalSnapshot]);

  const hasClinical = clinicalSnapshot.length > 0;

  // Quality performers — sorted, full list; display controlled by showAllPerf
  const qualPerformers = useMemo(() =>
    [...yearRecs].filter(r => r.qual_score != null).sort((a,b) => b.qual_score - a.qual_score),
  [yearRecs]);

  const displayedPerformers = showAllPerf ? qualPerformers : qualPerformers.slice(0, 10);
  const hasCahps = year >= 2021;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Year selector */}
      <div className="chart-card" style={{ padding: '14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--gray-600)' }}>Performance Year:</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {YEARS.map(y => (
              <button key={y} onClick={() => { setYear(y); setShowAll(false); }}
                style={{ padding: '5px 12px', borderRadius: 20, border: '1.5px solid',
                  borderColor: year === y ? 'var(--teal)' : 'var(--gray-300)',
                  background: year === y ? 'var(--teal-light)' : 'white',
                  color: year === y ? 'var(--teal-dark)' : 'var(--gray-600)',
                  cursor: 'pointer', fontSize: 12,
                  fontWeight: year === y ? 600 : 400, fontFamily: 'var(--font)' }}>
                {y}
              </button>
            ))}
          </div>
          {!hasCahps && (
            <span style={{ fontSize: 12, color: 'var(--amber)', background: 'var(--amber-light)',
              padding: '3px 10px', borderRadius: 12 }}>
              CAHPS measures available 2021+
            </span>
          )}
        </div>
      </div>

      {/* Quality vs savings scatter + distribution */}
      <div className="chart-grid-2">
        <div className="chart-card">
          <div className="chart-title">Quality Score vs. Savings Rate — PY {year}</div>
          <div className="chart-sub">Quality and savings are often correlated</div>
          <ResponsiveContainer width="100%" height={260}>
            <ScatterChart margin={{ top: 8, right: 20, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="qual_score" name="Quality Score" type="number" domain={[50, 105]}
                label={{ value: 'Quality Score', position: 'insideBottom', offset: -4, fontSize: 11 }}
                tick={{ fontSize: 11 }} />
              <YAxis dataKey="sav_rate_pct" name="Savings Rate" type="number"
                tickFormatter={v => `${v.toFixed(0)}%`} tick={{ fontSize: 11 }} width={42} />
              <Tooltip content={<ScatterTip />} />
              <ReferenceLine y={0} stroke="var(--gray-400)" />
              <Scatter data={qvsSavings}
                shape={(props) => {
                  const isPinned = pinnedRec && props.payload?.aco_id === pinnedRec.aco_id;
                  const opacity = pinnedRec ? (isPinned ? 1 : 0.15) : 0.65;
                  const r = isPinned ? 8 : 4;
                  const fill = props.payload.earn_save_loss > 0 ? COLORS.teal : COLORS.coral;
                  return (
                    <circle cx={props.cx} cy={props.cy} r={r} fill={fill}
                      opacity={opacity}
                      stroke={isPinned ? 'white' : 'none'}
                      strokeWidth={isPinned ? 2 : 0} />
                  );
                }}>
              </Scatter>
              {pinnedRec?.qual_score != null && pinnedRec?.sav_rate != null && (
                <ReferenceDot
                  x={pinnedRec.qual_score} y={pinnedRec.sav_rate * 100}
                  r={0} fill="none" stroke="none"
                  label={{ value: '▶ ' + (pinnedRec.aco_name || '').split(' ').slice(0,3).join(' '),
                    position: 'top', fontSize: 11, fontWeight: 700, fill: 'var(--teal-dark)',
                    style: { pointerEvents: 'none' } }}
                />
              )}
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
            🟢 Teal = earned savings &nbsp; 🟠 Coral = did not earn savings
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-title">Quality Score Distribution — PY {year}</div>
          <div className="chart-sub">Number of ACOs in each quality band</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={qualDist} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={36} />
              <Tooltip formatter={v => [v, 'ACOs']} />
              <Bar dataKey="count" name="ACOs" fill={COLORS.purple} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* CAHPS section */}
      {hasCahps && cahpsSnapshot.length > 0 && (
        <>
          <div className="chart-card">
            <div className="chart-title">CAHPS Patient Experience Scores — PY {year}</div>
            <div className="chart-sub">Average score across ACOs reporting (0–100 scale)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              {cahpsSnapshot.sort((a,b) => b.avg - a.avg).map(d => (
                <div key={d.field} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 230, fontSize: 12, color: 'var(--gray-700)', flexShrink: 0, textAlign: 'right' }}>
                    {d.label}
                  </div>
                  <div style={{ flex: 1, background: 'var(--gray-100)', borderRadius: 4, overflow: 'hidden', height: 20 }}>
                    <div style={{ width: `${d.avg}%`, height: '100%', borderRadius: 4,
                      background: d.avg >= 85 ? COLORS.teal : d.avg >= 70 ? COLORS.blue : COLORS.coral,
                      transition: 'width 0.4s ease' }} />
                  </div>
                  <div style={{ width: 52, fontSize: 13, fontWeight: 700, color: 'var(--navy)', textAlign: 'right', flexShrink: 0 }}>
                    {d.avg.toFixed(1)}
                  </div>
                  <div style={{ width: 44, fontSize: 11, color: 'var(--gray-400)', flexShrink: 0 }}>
                    n={d.n}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="chart-card">
            <div className="chart-title">CAHPS Trends — Program Average (2021–2024)</div>
            <div className="chart-sub">Four key patient experience measures over time</div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={cahpsTrend} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
                <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[55, 100]} width={36} />
                <Tooltip labelFormatter={l => `PY ${l}`}
                  formatter={(v, name) => [v?.toFixed(1), CAHPS_LABELS[name] || name]} />
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 11 }}
                  formatter={name => CAHPS_LABELS[name] || name} />
                {['cahps_1','cahps_3','cahps_4','cahps_9'].map((field, i) => {
                  const colors = [COLORS.teal, COLORS.navy, COLORS.amber, COLORS.purple];
                  return <Line key={field} dataKey={field} stroke={colors[i]}
                    strokeWidth={2} dot={{ r: 3 }} connectNulls />;
                })}
              </LineChart>
            </ResponsiveContainer>
            <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
              CAHPS measures available in the PUF starting PY 2021.
            </div>
          </div>
        </>
      )}

      {/* ── Clinical Quality Measures distribution ── */}
      {hasClinical ? (
        <div className="chart-card">
          <div style={{ marginBottom: 14 }}>
            <div className="chart-title">Clinical Quality Measure Distributions — PY {year}</div>
            <div className="chart-sub">
              Score distribution across all ACOs · box = IQR · line = median · whiskers = min/max
              {pinnedRec && <span style={{ marginLeft: 8, color: '#FF6B35', fontWeight: 600 }}>
                &nbsp;· 📌 {(pinnedRec.aco_name || '').split(' ').slice(0,3).join(' ')} shown in orange
              </span>}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {clinicalSnapshot.map(m => {
              const vals = yearRecs.map(r => r[m.field]).filter(v => v != null && !isNaN(Number(v)))
                .map(Number).sort((a,b) => a-b);
              if (!vals.length) return null;
              const min = vals[0], max = vals[vals.length-1];
              const q1  = vals[Math.floor(vals.length * 0.25)];
              const med = vals[Math.floor(vals.length * 0.50)];
              const q3  = vals[Math.floor(vals.length * 0.75)];
              const range = Math.max(0.001, max - min);
              const pct = v => ((v - min) / range) * 100;
              const pinnedVal = pinnedRec?.[m.field] != null ? Number(pinnedRec[m.field]) : null;
              return (
                <div key={m.field} style={{ padding: '10px 14px', background: 'var(--gray-50)',
                  borderRadius: 'var(--radius)', border: '1px solid var(--gray-100)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <div>
                      <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--navy)' }}>{m.label}</span>
                      {!m.higher && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--amber)',
                        background: 'var(--amber-light)', padding: '1px 6px', borderRadius: 8 }}>↓ lower = better</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)', display: 'flex', gap: 12 }}>
                      <span>Med: <strong style={{ color: 'var(--navy)' }}>{med.toFixed(1)}</strong></span>
                      {pinnedVal != null && (
                        <span style={{ color: '#FF6B35', fontWeight: 600 }}>
                          Pinned: {pinnedVal.toFixed(1)}
                          {m.higher ? (pinnedVal >= med ? ' ↑' : ' ↓') : (pinnedVal <= med ? ' ↑' : ' ↓')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ position: 'relative', height: 20, margin: '0 4px' }}>
                    <div style={{ position: 'absolute', top: 9, left: 0, right: 0, height: 2, background: 'var(--gray-300)', borderRadius: 1 }} />
                    <div style={{ position: 'absolute', top: 4, height: 12, borderRadius: 2,
                      left: `${pct(q1)}%`, width: `${Math.max(0.5, pct(q3) - pct(q1))}%`,
                      background: 'var(--teal)', opacity: 0.4 }} />
                    <div style={{ position: 'absolute', top: 2, height: 16, width: 3, borderRadius: 2,
                      left: `${pct(med)}%`, background: 'var(--teal)', transform: 'translateX(-50%)' }} />
                    <div style={{ position: 'absolute', top: 6, height: 8, width: 2, left: 0, background: 'var(--gray-400)' }} />
                    <div style={{ position: 'absolute', top: 6, height: 8, width: 2, right: 0, background: 'var(--gray-400)' }} />
                    {pinnedVal != null && pinnedVal >= min && pinnedVal <= max && (
                      <div style={{ position: 'absolute', top: -1, height: 22, width: 3, borderRadius: 2,
                        left: `${pct(pinnedVal)}%`, background: '#FF6B35',
                        transform: 'translateX(-50%)', boxShadow: '0 0 0 2px white' }} />
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 10, color: 'var(--gray-400)' }}>
                    <span>{min.toFixed(1)}</span><span>Q1: {q1.toFixed(1)}</span>
                    <span>Q3: {q3.toFixed(1)}</span><span>{max.toFixed(1)}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray-400)' }}>
            Clinical quality measures available PY 2021–2023 · select a year above to change view
          </div>
        </div>
      ) : (
        <div className="chart-card" style={{ textAlign: 'center', padding: 28, color: 'var(--gray-400)' }}>
          <div style={{ fontSize: 22, marginBottom: 6 }}>📋</div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {year < 2021 ? 'Clinical Quality Measures available PY 2021–2023'
              : `Clinical measure data not available for PY ${year}`}
          </div>
          <div style={{ fontSize: 12 }}>
            {year < 2021 ? 'Select PY 2021, 2022, or 2023 to view measure distributions.'
              : 'PY 2024 transitioned to updated measure reporting.'}
          </div>
        </div>
      )}

      {/* Top quality performers — expandable */}
      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div className="chart-title">
            {showAllPerf ? `All ${qualPerformers.length} ACOs` : 'Top 10 Quality Performers'} — PY {year}
          </div>
          <button onClick={() => setShowAll(v => !v)}
            style={{ padding: '5px 14px', borderRadius: 20, border: '1.5px solid var(--teal)',
              background: showAllPerf ? 'var(--teal-light)' : 'white',
              color: 'var(--teal-dark)', cursor: 'pointer', fontSize: 12,
              fontWeight: 500, fontFamily: 'var(--font)' }}>
            {showAllPerf ? '↑ Show top 10 only' : `↓ Show all ${qualPerformers.length} ACOs`}
          </button>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
              {['Rank','ACO Name','Quality Score','Savings Rate','Earned Savings','Met QPS'].map(h => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600,
                  color: 'var(--gray-600)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayedPerformers.map((r, i) => (
              <tr key={r.aco_id} style={{ background: i % 2 === 1 ? 'var(--gray-50)' : 'white',
                borderBottom: '1px solid var(--gray-100)' }}>
                <td style={{ padding: '7px 10px', fontWeight: 700, color: i < 3 ? 'var(--teal-dark)' : 'var(--gray-400)' }}>
                  #{i+1}
                </td>
                <td style={{ padding: '7px 10px', maxWidth: 240,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {navigateToACO ? (
                    <span
                      onClick={() => navigateToACO({ aco_id: r.aco_id, aco_name: r.aco_name,
                        aco_state: r.aco_state, most_recent_year: r.performance_year, years_count: 1 })}
                      style={{ fontWeight: 500, color: 'var(--teal-dark)', cursor: 'pointer' }}
                      onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={e => e.target.style.textDecoration = 'none'}
                    >
                      {r.aco_name || r.aco_id}
                    </span>
                  ) : (
                    <span style={{ fontWeight: 500, color: 'var(--navy)' }}>{r.aco_name || r.aco_id}</span>
                  )}
                </td>
                <td style={{ padding: '7px 10px', fontWeight: 700, color: 'var(--purple)' }}>
                  {r.qual_score?.toFixed(1) ?? '—'}
                </td>
                <td style={{ padding: '7px 10px', fontWeight: 600,
                  color: r.sav_rate > 0 ? 'var(--green)' : r.sav_rate < 0 ? 'var(--red)' : 'var(--gray-600)' }}>
                  {fmt.pct(r.sav_rate)}
                </td>
                <td style={{ padding: '7px 10px', fontWeight: 600,
                  color: r.earn_save_loss > 0 ? 'var(--green)' : 'var(--gray-600)' }}>
                  {fmt.dollars(r.earn_save_loss, 2)}
                </td>
                <td style={{ padding: '7px 10px' }}>
                  {r.met_qps === 1
                    ? <span style={{ color: 'var(--green)', fontWeight: 600 }}>✓ Yes</span>
                    : r.met_qps === 0
                    ? <span style={{ color: 'var(--red)' }}>✗ No</span>
                    : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!showAllPerf && qualPerformers.length > 10 && (
          <div style={{ textAlign: 'center', marginTop: 12, fontSize: 12, color: 'var(--gray-400)' }}>
            Showing top 10 of {qualPerformers.length} ACOs · click button above to see all
          </div>
        )}
      </div>

    </div>
  );
}
